import { useState, useEffect, useRef } from "react";
import { useLocation, useHistory, NavLink, Redirect } from "react-router-dom";
import axios from "axios";

const MAX_ATTEMPTS   = 30;
const POLL_INTERVAL  = 10000; // 10 seconds

const PaymentPending = () => {
    const location  = useLocation();
    const history   = useHistory();
    const query     = new URLSearchParams(location.search);
    const orderId   = query.get("id");
    const ref       = query.get("ref");

    const [attempts,       setAttempts]       = useState(0);
    const [stopped,        setStopped]        = useState(false);
    const [statusMessage,  setStatusMessage]  = useState("Verifying your payment with the bank...");

    const attemptRef  = useRef(0);
    const intervalRef = useRef(null);

    const checkStatus = async () => {

        attemptRef.current += 1;
        setAttempts(attemptRef.current);

        if (attemptRef.current >= MAX_ATTEMPTS) {
            clearInterval(intervalRef.current);
            setStopped(true);
            setStatusMessage("Payment verification timed out. Please contact support if an amount was deducted.");
            return;
        }

        try {
            const response = await axios.get(`/api/mat/payment-status/${orderId}`);
            const { status } = response.data;

            if (status === 'SUCCESS' || status === 'success') {
                clearInterval(intervalRef.current);
                history.push(`/payment/success?id=${orderId}${ref ? `&ref=${ref}` : ''}`);
            } else if (status === 'FAILED' || status === 'failed') {
                clearInterval(intervalRef.current);
                history.push(`/payment/failure?id=${orderId}&reason=PAYMENT_FAILED`);
            }
            // still pending — continue polling
        } catch (err) {
            // network/server error — continue polling silently
        }
    };

    useEffect(() => {
        if (!orderId) return;

        checkStatus();
        intervalRef.current = setInterval(checkStatus, POLL_INTERVAL);

        return () => clearInterval(intervalRef.current);
    }, []);

    const handleManualCheck = () => {
        if (attemptRef.current >= MAX_ATTEMPTS) {
            attemptRef.current = MAX_ATTEMPTS - 1;
            setStopped(false);
            setStatusMessage("Verifying your payment with the bank...");
        }
        checkStatus();
    };

    const timeRemaining = Math.max(0, (MAX_ATTEMPTS - attempts) * (POLL_INTERVAL / 1000));
    const progressPct   = Math.min(Math.max((attempts / MAX_ATTEMPTS) * 100, 5), 100);

    if (!orderId) return <Redirect to="/login" />;

    return (
        <div className="container margin-t50">
            <div className="row">
                <div className="col-md-6 col-md-offset-3 col-sm-8 col-sm-offset-2">
                    <div className="panel panel-default">
                        <div className="panel-body text-center">
                            {stopped ? (
                                <i
                                    className="fa fa-exclamation-circle"
                                    style={{ fontSize: '5rem', color: '#d9534f', marginBottom: '1rem', display: 'block' }}
                                ></i>
                            ) : (
                                <i
                                    className="fa fa-clock-o animated infinite pulse"
                                    style={{ fontSize: '5rem', color: '#f0ad4e', marginBottom: '1rem', display: 'block' }}
                                ></i>
                            )}

                            <h2 style={{ color: stopped ? '#d9534f' : '#f0ad4e', marginTop: 0, marginBottom: '0.5rem' }}>
                                {stopped ? 'Verification Timed Out' : 'Payment Processing'}
                            </h2>
                            <p className="text-muted">{statusMessage}</p>

                            {!stopped && (
                                <div style={{ marginTop: '1rem' }}>
                                    <div className="progress">
                                        <div
                                            className="progress-bar progress-bar-warning progress-bar-striped active"
                                            style={{ width: `${progressPct}%` }}
                                        ></div>
                                    </div>
                                    <small className="text-muted">
                                        Checked {attempts} of {MAX_ATTEMPTS} times
                                        &nbsp;&bull;&nbsp;
                                        ~{Math.floor(timeRemaining / 60)}m {timeRemaining % 60}s remaining
                                    </small>
                                </div>
                            )}

                            {(orderId || ref) && (
                                <div className="well well-sm" style={{ marginTop: '1.5rem', textAlign: 'left' }}>
                                    <table className="table table-condensed" style={{ marginBottom: 0 }}>
                                        <tbody>
                                            {orderId && (
                                                <tr>
                                                    <td><strong>Order ID</strong></td>
                                                    <td>{orderId}</td>
                                                </tr>
                                            )}
                                            {ref && (
                                                <tr>
                                                    <td><strong>Reference No.</strong></td>
                                                    <td>{ref}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            <div className="alert alert-warning" style={{ marginTop: '1rem', textAlign: 'left' }}>
                                <i className="fa fa-warning"></i>{' '}
                                <strong>Important:</strong> Do not make another payment. Your transaction is currently being processed.
                            </div>

                            <div style={{ marginTop: '1.5rem' }}>
                                <button
                                    onClick={handleManualCheck}
                                    className="btn btn-warning btn-block"
                                    style={{ marginBottom: '0.5rem' }}
                                    disabled={!orderId}
                                >
                                    <i className="fa fa-refresh"></i> Check Status Now
                                </button>
                                <NavLink to="/tee-booking" className="btn btn-default btn-block">
                                    <i className="fa fa-home"></i> Back to Home
                                </NavLink>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentPending;
